import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

import { BlockPreviewState } from './block-preview-state';

/** The widths the site is drawn at, scaled down to the panel (Desktop by default: §10 Q5). */
export const PREVIEW_DEVICES = [
  { label: 'Phone', width: 390, icon: 'pi pi-mobile' },
  { label: 'Tablet', width: 768, icon: 'pi pi-tablet' },
  { label: 'Desktop', width: 1280, icon: 'pi pi-desktop' },
];

/**
 * The preview panel (#263), presentational: the toolbar (width, Refresh, Close), the notes, and the site's document in
 * a sandboxed iframe drawn at the device's width and scaled to fit the panel, with the state's message over it. The
 * frame and Conductor can't measure each other, so the frame fills the panel's height and scrolls inside.
 */
@Component({
  selector: 'app-block-preview-panel',
  templateUrl: './block-preview-panel.component.html',
  styleUrls: ['./block-preview-panel.component.scss'],
})
export class BlockPreviewPanelComponent implements AfterViewInit, OnDestroy {
  @Input() state!: BlockPreviewState;
  @Input() srcdoc: SafeHtml | null = null;
  @Input() closable = true;
  @Output() refresh = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('viewport', { static: true }) viewport!: ElementRef<HTMLDivElement>;

  readonly devices = PREVIEW_DEVICES;
  width = 1280;
  scale = 1;
  frameHeight = 0;

  private resize: ResizeObserver | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.resize = new ResizeObserver(() => {
      this.fit();
      this.cdr.detectChanges();
    });
    this.resize.observe(this.viewport.nativeElement);
  }

  ngOnDestroy(): void {
    this.resize?.disconnect();
  }

  setWidth(width: number): void {
    this.width = width;
    this.fit();
  }

  /** The render is behind something the person should read: dimmed under the message. */
  get dimmed(): boolean {
    return this.state.phase !== 'ready' && this.state.phase !== 'loading';
  }

  /** The thin bar over the last render while the next one loads. */
  get updating(): boolean {
    return this.state.phase === 'loading' && !!this.state.html;
  }

  private fit(): void {
    const box = this.viewport.nativeElement;
    this.scale = Math.min(1, box.clientWidth / this.width);
    this.frameHeight = Math.round(box.clientHeight / this.scale);
  }
}
